import { useForm, useField, FormContextKey } from "vee-validate";
import { provide, watch, onUnmounted } from "vue";
import { injectWithSelf, matcher } from "~/utils/miscellaneous";

function getFieldNameVal(fieldName) {
  return fieldName == null || typeof fieldName == "string"
    ? fieldName
    : fieldName.value;
}

function getElementRefVal(elementRef) {
  return elementRef == null ||
    elementRef instanceof HTMLElement ||
    elementRef instanceof Array
    ? elementRef
    : elementRef.value;
}

function getElementNode(elementRef) {
  return elementRef == null || elementRef instanceof HTMLElement
    ? elementRef
    : elementRef instanceof Array
    ? elementRef[0]
    : getElementNode(elementRef.value);
}

class MyFormContext {
  constructor() {
    this.__schemaElements = [];
  }

  useSchemaElement(fieldName, fieldRef) {
    watch(
      fieldRef,
      (val) => {
        this.removeSchemaElement(fieldName);
        this.addSchemaElement(fieldName, val);
      },
      { deep: true }
    );
    onUnmounted(() => {
      this.removeSchemaElement(fieldName);
    });
  }

  _getSchemaElementIndex(fieldNameVal) {
    return this.__schemaElements.findIndex(
      (obj) => obj.schemaName == fieldNameVal
    );
  }

  getSchemaElement(fieldName) {
    return this.__schemaElements[
      this._getSchemaElementIndex(getFieldNameVal(fieldName))
    ];
  }

  removeSchemaElement(fieldName) {
    let fieldNameVal = getFieldNameVal(fieldName);

    if (!fieldNameVal) return;

    let foundSchemaIndex = this._getSchemaElementIndex(fieldNameVal);

    if (foundSchemaIndex != -1)
      this.__schemaElements.splice(foundSchemaIndex, 1);
  }

  addSchemaElement(fieldName, elementRef) {
    let fieldNameVal = getFieldNameVal(fieldName);
    let elementRefVal = getElementRefVal(elementRef);
    let elementNode = getElementNode(elementRefVal);

    if (!fieldNameVal || !elementRefVal) return;

    let i = 0;
    while (
      i < this.__schemaElements.length &&
      //check is "schemaElement"'s element node is before the "elementNode" in DOM tree
      getElementNode(this.__schemaElements[i].element).compareDocumentPosition(
        elementNode
      ) == Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      i++;
    }

    this.__schemaElements.splice(i, 0, {
      schemaName: fieldNameVal,
      element: elementRefVal,
    });
  }

  getTopSchemaElement(schemaNames) {
    let firstSchemaIndex = null;

    schemaNames.forEach((schemaName) => {
      let curSchemaIndex = this._getSchemaElementIndex(schemaName);

      if (
        (firstSchemaIndex == null || firstSchemaIndex > curSchemaIndex) &&
        curSchemaIndex != -1
      )
        firstSchemaIndex = curSchemaIndex;
    });

    return firstSchemaIndex != null
      ? this.__schemaElements[firstSchemaIndex]
      : null;
  }
}

export function useMyForm(...args) {
  const formContext = useForm.call(this, ...args);

  const privateFormContext = injectWithSelf(FormContextKey);

  const myFormContext = new MyFormContext();
  provide("myFormContext", myFormContext);

  async function validate(...validateArgs) {
    const res = await formContext.validate(...validateArgs);
    getElementNode(
      myFormContext.getTopSchemaElement(Object.keys(res.errors))?.element
    )?.focus();

    return res;
  }

  const { fieldsByPath } = privateFormContext;

  function resetFieldByPath(fieldPath, state) {
    fieldsByPath.value[fieldPath].resetField(state);
  }

  function resetField(field, state) {
    const fieldsByPathKeys = Object.keys(fieldsByPath.value);
    const fieldRegexPattern = field.match(/^\/(.+)\/$/)?.[1];
    const isMatch = matcher(fieldRegexPattern ? new RegExp(fieldRegexPattern) : field);

    fieldsByPathKeys
      .filter((fieldPathKey) => isMatch(fieldPathKey))
      .forEach((fieldPathKey) => resetFieldByPath(fieldPathKey, state));
  }

  function resetFields(fields) {
    Object.entries(fields).forEach(([field, state]) =>
      resetField(field, state)
    );
  }

  return {
    ...formContext,
    validate,
    resetFields,
  };
}

export function useMyField(fieldName, rules, opts, fieldRef) {
  const fieldContext = useField(fieldName, rules, opts);

  if (fieldRef) {
    const myFormContext = injectWithSelf("myFormContext");
    myFormContext.useSchemaElement(fieldName, fieldRef);
  }

  return fieldContext;
}
